import {
  PlatformPublication,
  RELEASE_STAGES,
  ReleaseStage,
  TiVoPublications,
} from "hooks/tivoApps";
import { TiVoDevicePlatform } from "hooks/tivoDevices";

import { PATHS } from "./constants";
import { TiVoPublicationsForm } from "./types";

export function getPageMode() {
  const url = location.pathname.split("/");
  return url[3] || PATHS.SUMMARY;
}

export function getReleaseStagePublication(
  publication: TiVoPublications,
  platformId: string,
  oemId: string,
  clientTypeId?: string
) {
  const platformPub = publication.platforms.find(
    ({ platformId: id }) => id === platformId
  );
  const oemPub = platformPub?.publications[oemId];
  if (!oemPub) {
    return [];
  }
  return RELEASE_STAGES.reduce<ReleaseStage[]>((acc, cur) => {
    const rsPub = oemPub[cur];
    if (!rsPub) {
      return acc;
    }
    if (clientTypeId && rsPub.length > 0 && !rsPub.includes(clientTypeId)) {
      return acc;
    }
    return [...acc, cur];
  }, []);
}

export function getPublicationsInitialValues(
  platforms: TiVoDevicePlatform[],
  publications: TiVoPublications[],
  filterByPlatformId?: string
) {
  const form: TiVoPublicationsForm = {};
  for (const pub of publications) {
    const pubId = pub.id;
    if (!form[pubId]) {
      form[pubId] = {};
    }
    for (const platformPub of pub.platforms) {
      const platformId = platformPub.platformId;
      const targetByClientTypes = platforms.find(
        ({ id }) => id === platformId
      )?.targetedByClientType;
      if (filterByPlatformId && platformId !== filterByPlatformId) {
        continue;
      }
      if (!form[pubId][platformId]) {
        form[pubId][platformId] = {};
      }
      for (const [oemId, oemPub] of Object.entries(platformPub.publications)) {
        const oem = pub.oems?.find(({ id }) => id === oemId);
        if (!oem) {
          console.error(`Ignoring an unknown OEM: ${oemId}`);
          continue;
        }
        if (!form[pubId][platformId][oemId]) {
          form[pubId][platformId][oemId] = {};
        }
        if (
          !targetByClientTypes ||
          !oem.clientTypes ||
          oem.clientTypes.length === 0
        ) {
          for (const rs of RELEASE_STAGES) {
            form[pubId][platformId][oemId][rs] = rs in oemPub;
          }
          continue;
        }
        for (const client of oem.clientTypes) {
          const clientId = client.id;
          if (!(form[pubId][platformId][oemId] as any)[clientId]) {
            (form[pubId][platformId][oemId] as any)[clientId] = {};
          }
          for (const rs of RELEASE_STAGES) {
            const rsPub = oemPub[rs];
            (form[pubId][platformId][oemId] as any)[clientId][rs] = Boolean(
              rsPub && (rsPub.length === 0 || rsPub.includes(clientId))
            );
          }
        }
      }
    }
  }
  return form;
}

export function adaptPublicationsToRequest(form: TiVoPublicationsForm) {
  const result: Pick<TiVoPublications, "id" | "platforms">[] = [];
  for (const [pubId, pub] of Object.entries(form)) {
    const pformPubs: PlatformPublication[] = [];
    for (const [pformId, pformPub] of Object.entries(pub)) {
      const oemPubs: PlatformPublication["publications"] = {};
      for (const [oemId, oemPub] of Object.entries(pformPub)) {
        if (!oemPubs[oemId]) {
          oemPubs[oemId] = {};
        }
        for (const [key, value] of Object.entries(oemPub)) {
          if (RELEASE_STAGES.includes(key as ReleaseStage)) {
            const rs = key as ReleaseStage;
            const enabled = value as boolean;
            if (enabled) {
              oemPubs[oemId][rs] = [];
            }
          } else {
            const ctId = key;
            const ctPub = value as Record<ReleaseStage, boolean>;
            for (const rs of RELEASE_STAGES) {
              const enabled = ctPub[rs];
              if (enabled) {
                if (!oemPubs[oemId][rs]) {
                  oemPubs[oemId][rs] = [];
                }
                oemPubs[oemId][rs].push(ctId);
              }
            }
          }
        }
      }
      pformPubs.push({
        platformId: pformId,
        publications: oemPubs,
      });
    }
    result.push({
      id: pubId,
      platforms: pformPubs,
    });
  }
  return result;
}
